import { useState, type FormEvent } from "react";
import { footerColumns } from "@/mocks/gossip";

const SUBMIT_ADDR = "https://readdy.ai/api/form/dahh68bp14a1h3mr6mp0";

type FormStatus = "idle" | "loading" | "success" | "error";

export default function SubscribeFooter() {
  const [status, setStatus] = useState<FormStatus>("idle");
  const [formError, setFormError] = useState("");
  const [message, setMessage] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError("");
    setMessage("");

    const form = event.currentTarget;
    const payload = new FormData(form);

    const honeypot = String(payload.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setMessage("订阅成功，瓜田快讯会第一时间送达。");
      form.reset();
      return;
    }
    payload.delete("website_alt");

    const email = String(payload.get("email") || "").trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus("error");
      setFormError("请填写有效的邮箱地址");
      return;
    }

    setStatus("loading");
    try {
      const body = new URLSearchParams();
      payload.forEach((value, key) => {
        if (typeof value === "string") body.append(key, value);
      });

      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = String(
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "",
      );
      const codeOk = parsed?.code === "OK";
      const isSpam = /spam/i.test(serverMsg);

      if (!response.ok || !codeOk || isSpam) {
        setStatus("error");
        setFormError(
          serverMsg && !isSpam ? serverMsg : "订阅失败，请检查内容后稍后重试",
        );
        return;
      }

      setStatus("success");
      setMessage("订阅成功，瓜田快讯会第一时间送达。");
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络异常，请稍后重试");
    }
  };

  return (
    <>
      <section id="subscribe" className="w-full bg-secondary-100 py-14 md:py-20">
        <div className="mx-auto w-full max-w-[1200px] px-4 md:px-6">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_1.1fr] lg:items-center lg:gap-14">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-secondary-300 bg-background-50 px-4 py-1.5 text-xs font-semibold text-secondary-800">
                <i className="ri-mail-star-line" />
                瓜田快讯 · 每周精选
              </span>
              <h2 className="mt-5 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-4xl">
                不想错过下一波热议？
                <br />
                留下邮箱，我们帮你盯热榜
              </h2>
              <p className="mt-4 max-w-lg text-sm leading-relaxed text-foreground-600 md:text-base">
                每周一期，把当周与
                <strong className="font-semibold text-foreground-900">骚女qq</strong>
                相关的传闻、话题与瓜田合集做成一份清单发给你。只汇总讨论，不站队、不定性。
              </p>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
              <form
                id="melon-subscribe-form"
                data-readdy-form
                noValidate
                onSubmit={handleSubmit}
                className="flex flex-col gap-4"
              >
                <div>
                  <label
                    htmlFor="melon-email"
                    className="mb-2 block text-xs font-semibold text-foreground-700"
                  >
                    邮箱地址
                  </label>
                  <div className="flex items-center gap-2 rounded-md border border-background-200 bg-background-50 px-3.5 py-3 focus-within:border-primary-400">
                    <i className="ri-mail-line text-base text-foreground-400" />
                    <input
                      id="melon-email"
                      type="email"
                      name="email"
                      required
                      autoComplete="email"
                      placeholder="you@example.com"
                      className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="melon-topic"
                    className="mb-2 block text-xs font-semibold text-foreground-700"
                  >
                    最想看哪一类内容
                  </label>
                  <div className="flex items-center gap-2 rounded-md border border-background-200 bg-background-50 px-3.5 py-3">
                    <i className="ri-price-tag-3-line text-base text-foreground-400" />
                    <select
                      id="melon-topic"
                      name="topic"
                      defaultValue="全部内容"
                      className="w-full bg-transparent text-sm text-foreground-900 outline-none"
                    >
                      <option value="全部内容">全部内容</option>
                      <option value="传闻与澄清">传闻与澄清</option>
                      <option value="热议话题盘点">热议话题盘点</option>
                      <option value="瓜田合集">瓜田合集</option>
                      <option value="评论区观察">评论区观察</option>
                    </select>
                  </div>
                </div>

                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  readOnly
                  aria-hidden="true"
                  className="site-note-check"
                />

                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {status === "loading" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-base" />
                      提交中…
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-fill text-base" />
                      免费订阅快讯
                    </>
                  )}
                </button>

                {status === "success" && message ? (
                  <p className="flex items-start gap-2 rounded-md bg-accent-50 px-3.5 py-3 text-xs text-accent-900 md:text-sm">
                    <i className="ri-checkbox-circle-fill mt-0.5 text-base text-accent-600" />
                    {message}
                  </p>
                ) : null}

                {status === "error" && formError ? (
                  <p className="flex items-start gap-2 rounded-md bg-primary-50 px-3.5 py-3 text-xs text-primary-800 md:text-sm">
                    <i className="ri-error-warning-fill mt-0.5 text-base text-primary-600" />
                    {formError}
                  </p>
                ) : null}

                <p className="text-[11px] leading-relaxed text-foreground-400">
                  订阅即表示你同意接收本站邮件，可随时退订。我们不会把你的邮箱用于其他用途。
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      <footer className="w-full border-t border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1200px] px-4 py-12 md:px-6 md:py-16">
          <div className="grid grid-cols-1 gap-10 md:grid-cols-[1.3fr_1fr_1fr_1fr] md:gap-8">
            <div>
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-500 font-heading text-lg font-black text-background-50">
                  瓜
                </span>
                <span className="leading-tight">
                  <span className="block font-heading text-base font-black text-foreground-950">
                    骚女qq
                  </span>
                  <span className="block text-[10px] tracking-[0.22em] text-foreground-500">
                    MELON FILE
                  </span>
                </span>
              </div>
              <p className="mt-4 max-w-xs text-xs leading-relaxed text-foreground-600 md:text-sm">
                专注整理与骚女qq相关的网络传闻、话题与热议事件，做一份看得明白的吃瓜档案。
              </p>
              <div className="mt-5 flex items-center gap-2.5">
                {["ri-wechat-line", "ri-weibo-line", "ri-rss-line", "ri-message-3-line"].map(
                  (icon) => (
                    <button
                      key={icon}
                      type="button"
                      aria-label="社交入口"
                      className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-200 bg-background-50 text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600"
                    >
                      <i className={`${icon} text-base`} />
                    </button>
                  ),
                )}
              </div>
            </div>

            {footerColumns.map((col) => (
              <div key={col.title}>
                <h3 className="font-heading text-sm font-bold text-foreground-950">{col.title}</h3>
                <ul className="mt-4 flex flex-col gap-2.5">
                  {col.links.map((link) => (
                    <li key={link.label}>
                      <a
                        href={link.href}
                        className="text-xs text-foreground-600 transition-colors hover:text-primary-600 md:text-sm"
                      >
                        {link.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-10 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
            <p className="flex items-start gap-2 text-[11px] leading-relaxed text-foreground-500 md:text-xs">
              <i className="ri-shield-check-line mt-0.5 text-sm text-secondary-500" />
              免责声明：本站内容均为网络公开讨论、网友投稿与传闻的整理汇总，可能包含未经证实的说法，
              不代表对任何人或事件的真实性认定，也不构成任何评价。请理性吃瓜，勿对号入座、勿传播不实信息。
            </p>
          </div>

          <div className="mt-8 flex flex-col items-center justify-between gap-4 border-t border-background-200 pt-6 sm:flex-row">
            <p className="text-[11px] text-foreground-500 md:text-xs">
              © 2026 骚女qq · 传闻爆料瓜田 话题热议事件汇总盘点
            </p>
            <p className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[11px] text-foreground-500 md:text-xs">
              <a href="#top" className="whitespace-nowrap hover:text-primary-600">
                内容说明
              </a>
              <a href="#top" className="whitespace-nowrap hover:text-primary-600">
                投稿须知
              </a>
              <a href="#top" className="whitespace-nowrap hover:text-primary-600">
                版权与免责
              </a>
            </p>
          </div>
        </div>
      </footer>
    </>
  );
}